import { useState, useEffect, useCallback, useRef } from "react";
import { devLog } from "../../../utils/devLog";
import { useStyles } from "../../../context/StyleContext";
import { useTranslation } from "../../../hooks/useTranslation";
import { useCheckout } from "../../../context/CheckoutContext";
import { fetchGatewayConnection, processPayment, recordCitGatewayDecline } from "../../../api/api";
import { fetchSession } from "../../../utils/paymentSession";
import { formatFrequencyUnit } from "../../../utils/commonUtils";
import { safeRedirect } from "../../../utils/redirectUrlBuilder";
import { formatPaymentError } from "../../../utils/formatPaymentError";
import {
  handleCitPaymentApiResponse,
  applyCitDeclineOrErrorUi,
  recordAndHandleCitGatewayDecline,
  CIT_OUTCOME,
} from "../../../utils/paymentOutcome";
import { useNuveiCard } from "../../../hooks/useNuveiCard";
import NuveiCardFormView from "./NuveiCardFormView";
import { toast } from "react-toastify";

/**
 * NuveiCardForm
 *
 * Logic component for Nuvei card payments.
 *
 * Flow:
 *  1. useNuveiCard calls POST /nuvei/open-order on mount to obtain a session token,
 *     then loads safecharge.js and mounts the three Nuvei field iframes.
 *  2. When triggerPaymentMethodEvent fires (Pay button click from parent), handlePayNow() runs.
 *  3. safecharge.createPayment() is called — Nuvei handles 3DS natively.
 *  4. On APPROVED/SUCCESS, POST /process-payment with additionalData.sessionToken.
 *  5. Result is routed via onParentCallback (embedded) or status modal (standalone).
 */
const NuveiCardForm = ({
  formData,
  itemDetails,
  billingPlatformId,
  routingEntries,
  planId,
  platformItemDetails,
  checkoutPageConfDto,
  checkoutProcessDto,
  transparent,
  prefetchedGatewayConnection,
  tenantId,

  // Altruon embedded-checkout variant
  isEmbeddedCheckout = false,
  onParentCallback,
  sessionId,
}) => {
  const { t } = useTranslation();
  const { styles } = useStyles();
  const {
    triggerPaymentMethodEvent,
    resetTrigger,
    clientIp,
    clientCountry,
    paymentMethodName,
    platformName,
    estimatedOrder,
    isHostedPageMode,
  } = useCheckout();

  const [loading, setLoading]                     = useState(false);
  const [error, setError]                         = useState("");
  const [cardholderName, setCardholderName]        = useState("");
  const [gatewayConnection, setGatewayConnection]  = useState(
    prefetchedGatewayConnection || null
  );
  const [showStatusModal, setShowStatusModal]      = useState(false);
  const [transactionStatus, setTransactionStatus]  = useState("");
  const [redirectUrl, setRedirectUrl]              = useState(null);
  const [hostedPageRedirect, setHostedPageRedirect]= useState(null);
  const [transactionId, setTransactionId]          = useState(null);
  const [showProcessingModal, setProcessingModal]  = useState(false);

  const isProcessingRef = useRef(false);
  const tRef = useRef(t);
  const prefetchedGatewayConnectionRef = useRef(prefetchedGatewayConnection);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  useEffect(() => {
    prefetchedGatewayConnectionRef.current = prefetchedGatewayConnection;
  }, [prefetchedGatewayConnection]);

  const nuveiRoutingId = routingEntries?.find(
    (e) => e.paymentMethod === "card" && e.platform === "nuvei"
  )?.id ?? null;

  // ── Resolve gateway connection once per routing id ───────────────
  // Do not depend on `t` — translations load async and were re-firing this GET.
  useEffect(() => {
    if (gatewayConnection) return;

    const prefetched = prefetchedGatewayConnectionRef.current;
    if (prefetched) {
      setGatewayConnection(prefetched);
      return;
    }

    if (!nuveiRoutingId) return;

    let cancelled = false;
    fetchGatewayConnection({ id: nuveiRoutingId }, tRef.current)
      .then((connection) => {
        if (!cancelled) setGatewayConnection(connection);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("❌ Failed to fetch Nuvei gateway connection:", err);
      });

    return () => {
      cancelled = true;
    };
  }, [nuveiRoutingId, gatewayConnection]);

  // ── Resolve redirect URL from checkoutPageConfDto ────────────────
  useEffect(() => {
    if (checkoutPageConfDto) {
      setRedirectUrl(checkoutPageConfDto);
    }
  }, [checkoutPageConfDto]);

  // Pre-fill cardholder name from formData when available
  useEffect(() => {
    if (!cardholderName && formData?.firstName) {
      const name = [formData.firstName, formData.lastName].filter(Boolean).join(" ");
      setCardholderName(name);
    }
  }, [formData, cardholderName]);

  // ── useNuveiCard hook ────────────────────────────────────────────
  const {
    ready: cardReady,
    error: sdkError,
    loading: sdkLoading,
    sessionToken,
    zeroAuth,
    cardNumberRef,
    cardExpiryRef,
    cardCvcRef,
    createPayment: nuveiCreatePayment,
    refreshSession: refreshNuveiSession,
    reinitialize: reinitializeNuveiCard,
  } = useNuveiCard({
    sessionId,
    itemDetails,
    platformItemDetails,
    formData,
    styles,
    onReady: () => {
      devLog("✅ Nuvei Fields ready");
    },
    onError: (msg) => {
      console.error("❌ Nuvei Fields error:", msg);
      setError(msg);
    },
  });

  // ── Build process-payment payload ────────────────────────────────
  const buildPayload = useCallback(
    (latestSession, overrideSessionToken) => {
      const sc = latestSession?.customerData || {};
      const sb = sc?.billingAddress || {};
      return {
        paymentData: {
          gatewayConnectionId: gatewayConnection?.id,
          gatewayName:         gatewayConnection?.platform || "nuvei",
          paymentMethod:       "card",
          additionalData: {
            sessionToken: overrideSessionToken || sessionToken,
          },
        },
        customerData: {
          firstName: formData?.firstName || sc.firstName || "",
          lastName:  formData?.lastName  || sc.lastName  || "",
          email:     formData?.email     || sc.email     || "",
          phone:     formData?.phone     || sc.phone     || "",
          ip:        clientIp || "127.0.0.1",
          billingAddress: {
            country: formData?.billingAddress?.country    || sb.country || clientCountry || "US",
            zipCode: formData?.billingAddress?.zipCode    || sb.zipCode || "",
            street:  formData?.billingAddress?.street || sb.street || "",
            city:    formData?.billingAddress?.city       || sb.city    || "",
            state:   formData?.billingAddress?.state      || sb.state   || "",
          },
        },
        billingData: {
          planId,
          billingPlatformId,
          billingPlatformName: itemDetails?.billingPlatformName || latestSession?.billingData?.billingPlatformName || "",
          frequency: itemDetails?.periodUnit
            ? formatFrequencyUnit(itemDetails.periodUnit)
            : latestSession?.billingData?.frequency || "",
        },
      };
    },
    [gatewayConnection, sessionToken, formData, clientIp, clientCountry, planId, billingPlatformId, itemDetails]
  );

  // ── Main payment handler ─────────────────────────────────────────
  const handlePayNow = useCallback(async () => {
    if (isProcessingRef.current) {
      console.warn("Payment already in progress");
      return;
    }
    isProcessingRef.current = true;
    setError("");

    if (!isEmbeddedCheckout) {
      setProcessingModal(true);
    }

    try {
      if (!cardReady) {
        throw new Error(t("paymentForms.card.cardNotReady") || "Card form not ready");
      }
      if (!sessionToken) {
        throw new Error("Nuvei session not initialized");
      }

      // Fetch session first — in the embedded checkout formData is not populated
      // from the parent page, so latestSession.customerData is the source of truth.
      const latestSession = await fetchSession(sessionId);
      const sessionCustomer  = latestSession?.customerData || {};
      const sessionBilling   = sessionCustomer?.billingAddress || {};

      // Resolve every field: formData (if present) > session > safe default
      const resolvedEmail     = formData?.email                      || sessionCustomer.email     || "";
      const resolvedFirstName = formData?.firstName                  || sessionCustomer.firstName || "";
      const resolvedLastName  = formData?.lastName                   || sessionCustomer.lastName  || "";
      const resolvedPhone     = formData?.phone                      || sessionCustomer.phone     || "";
      const resolvedCountry   = formData?.billingAddress?.country    || sessionBilling.country    || clientCountry || "US";
      const resolvedCity      = formData?.billingAddress?.city       || sessionBilling.city       || "";
      const resolvedState     = formData?.billingAddress?.state      || sessionBilling.state      || "";
      const resolvedZipCode   = formData?.billingAddress?.zipCode    || sessionBilling.zipCode    || "";
      const resolvedStreet    = formData?.billingAddress?.street || sessionBilling.street   || "";
      const resolvedAddress2  = formData?.billingAddress?.addressLine2 || sessionBilling.addressLine2 || "";

      if (!resolvedEmail) {
        throw new Error(
          t("checkout.validation.required") ||
          "Customer email is required for payment"
        );
      }

      setLoading(true);

      const billingAddress = {
        firstName: resolvedFirstName,
        lastName:  resolvedLastName,
        email:     resolvedEmail,
        phone:     resolvedPhone,
        country:   resolvedCountry,
        city:      resolvedCity,
        state:     resolvedState,
        zipCode:   resolvedZipCode,
        street:    resolvedStreet,
        address2:  resolvedAddress2,
      };

      // 1. Refresh the Nuvei openOrder with the latest, real amount so that
      //    createPayment charges the correct total.
      //
      //    The openOrder created at mount time is based on the raw plan +
      //    addon amount. If the customer then redeems a coupon (or the
      //    estimate is recomputed for any other reason — country-based tax,
      //    addon changes, etc.) the Nuvei order would otherwise be stuck
      //    on the original amount and the backoffice would show the
      //    pre-discount charge even though Altruon's transaction records
      //    the discounted total.
      //
      //    `estimatedOrder.futureInvoiceAmountDue` is the final chargeable
      //    total from the billing platform estimate (post-coupon, post-tax)
      //    in major units — exactly what Nuvei's openOrder expects.
      //    A zero-authorization order has no amount to reconcile, so it is used as opened.
      let activeSessionToken = sessionToken;
      if (!zeroAuth) {
        try {
          const estimatedTotalRaw = estimatedOrder?.futureInvoiceAmountDue;
          const estimatedTotal =
            estimatedTotalRaw != null ? Number(estimatedTotalRaw) : null;
          const refreshed = await refreshNuveiSession(
            estimatedTotal != null && estimatedTotal > 0
              ? { amount: estimatedTotal }
              : {}
          );
          if (refreshed) activeSessionToken = refreshed;
        } catch (refreshErr) {
          console.warn("Nuvei session refresh failed, continuing with existing token:", refreshErr);
        }
      }

      // 2. Call Nuvei createPayment — 3DS handled natively.
      //    cardHolderName is a top-level field per Nuvei SDK contract.
      //    Returns a Promise wrapping the SDK callback.
      const resolvedName = cardholderName || `${resolvedFirstName} ${resolvedLastName}`.trim();
      const nuveiResult = await nuveiCreatePayment(resolvedName, billingAddress);
      devLog('Nuvei createPayment full result:', nuveiResult);

      // Nuvei uses { result: "APPROVED" } in callback responses
      const status = nuveiResult?.result || nuveiResult?.status;
      const { transactionId, errCode, errorDescription, reason, message: nuveiMessage } = nuveiResult || {};

      if (status !== "APPROVED" && status !== "SUCCESS") {
        const detail =
          errorDescription ||
          reason ||
          nuveiMessage ||
          (errCode ? `Error code: ${errCode}` : null);
        const declineMessage = formatPaymentError(
          detail,
          t,
          t("messages.error.paymentFailed") || "Payment declined"
        );
        setError(declineMessage);

        const citUi = {
          isHostedPageMode,
          isEmbeddedCheckout,
          t,
          setError,
          setTransactionStatus,
          setShowStatusModal,
          onParentCallback,
          toast,
        };

        const declinePayload = buildPayload(latestSession);
        await recordAndHandleCitGatewayDecline(
          recordCitGatewayDecline,
          billingPlatformId,
          sessionId,
          {
            checkoutRequest: declinePayload,
            failureMessage: detail || declineMessage,
            statusDetail: detail || declineMessage,
            statusCode: errCode != null ? String(errCode) : undefined,
            idAtPaymentGateway: transactionId || undefined,
            gatewayResponse: nuveiResult || undefined,
          },
          citUi
        );

        try {
          await reinitializeNuveiCard();
        } catch (reinitErr) {
          console.warn("Nuvei reinitialize after decline failed:", reinitErr);
        }
        return;
      }

      // 2. Notify parent for embedded checkout (pre-process step)
      if (isEmbeddedCheckout) {
        onParentCallback?.({
          type: "finalizeFormData",
          data: {
            paymentMethod: "card",
            gatewayName:   "nuvei",
            sessionToken,
            transactionId,
          },
        });
        await new Promise((r) => setTimeout(r, 800));
      }

      // 3. Call Altruon /process-payment (reuse the already-fetched session)
      const payload = buildPayload(latestSession, activeSessionToken);
      const res = await processPayment(billingPlatformId, sessionId, payload);

      const citUi = {
        isHostedPageMode,
        isEmbeddedCheckout,
        t,
        setError,
        setTransactionStatus,
        setShowStatusModal,
        onParentCallback,
        toast,
      };
      const { handled } = handleCitPaymentApiResponse(res, citUi);
      if (handled) return;

      if (res.status === 200) {
        // Capture transaction + backend-computed hosted redirect so the
        // TransactionStatusModal can append trx_id/status via buildRedirectUrl.
        // Parity with DlocalCardForm / CheckoutComCardForm / AdyenCardForm.
        if (res.data?.transactionId) {
          setTransactionId(res.data.transactionId);
        }
        if (res.data?.hosted_redirect_page) {
          setHostedPageRedirect(res.data.hosted_redirect_page);
        }

        const redirectLink = res.data?.redirect_url || null;

        if (isEmbeddedCheckout) {
          if (redirectLink) {
            onParentCallback?.({
              type: "actionRequired",
              action: "REDIRECT",
              url: redirectLink,
            });
          } else {
            onParentCallback?.({
              type: "success",
              transactionId: res.data?.transactionId,
            });
          }
        } else if (redirectLink) {
          // Hosted page: a gateway redirect (e.g. 3DS) means the payment isn't
          // final yet — navigate the current tab to the gateway instead of
          // showing a premature success modal. Nuvei returns the consumer to
          // our return URL, where the final status is resolved.
          safeRedirect(redirectLink, { target: "self" });
        } else {
          setTransactionStatus("success");
          setShowStatusModal(true);
        }
      } else {
        throw new Error(t("messages.error.somethingWrong") || "Payment failed");
      }
    } catch (err) {
      const citUi = {
        isHostedPageMode,
        isEmbeddedCheckout,
        t,
        setError,
        setTransactionStatus,
        setShowStatusModal,
        onParentCallback,
        toast,
      };
      if (err?.response && handleCitPaymentApiResponse(err.response, citUi).handled) return;
      applyCitDeclineOrErrorUi(
        {
          outcome: CIT_OUTCOME.ERROR,
          message: formatPaymentError(err?.message, t),
        },
        citUi
      );
    } finally {
      setLoading(false);
      isProcessingRef.current = false;
      if (!isEmbeddedCheckout) {
        setProcessingModal(false);
      }
    }
  }, [
    cardReady,
    sessionToken,
    zeroAuth,
    cardholderName,
    formData,
    nuveiCreatePayment,
    refreshNuveiSession,
    reinitializeNuveiCard,
    buildPayload,
    billingPlatformId,
    sessionId,
    isEmbeddedCheckout,
    onParentCallback,
    estimatedOrder,
    t,
  ]);

  // ── Listen for triggerPaymentMethodEvent from CheckoutContext ────
  useEffect(() => {
    if (
      triggerPaymentMethodEvent &&
      (isEmbeddedCheckout ||
        (platformName === "nuvei" && paymentMethodName === "card"))
    ) {
      handlePayNow();
      resetTrigger();
    }
  }, [
    triggerPaymentMethodEvent,
    platformName,
    paymentMethodName,
    isEmbeddedCheckout,
    handlePayNow,
    resetTrigger,
  ]);

  return (
    <NuveiCardFormView
      t={t}
      loading={loading}
      error={error || sdkError}
      sdkLoading={sdkLoading}
      cardReady={cardReady}
      cardholderName={cardholderName}
      onCardholderNameChange={setCardholderName}
      cardNumberRef={cardNumberRef}
      cardExpiryRef={cardExpiryRef}
      cardCvcRef={cardCvcRef}
      showProcessingModal={showProcessingModal}
      setProcessingModal={setProcessingModal}
      showStatusModal={showStatusModal}
      setShowStatusModal={setShowStatusModal}
      transactionStatus={transactionStatus}
      transactionId={transactionId}
      hostedPageRedirect={hostedPageRedirect}
      checkoutConfDto={redirectUrl}
    />
  );
};

export default NuveiCardForm;
